/**
 * Central updater (LIVE L3, release 0.2.0; docs/plans/live-updater.md).
 *
 * The updater is a container on central with Docker control. It finds signed releases (GitLab
 * job webhook relayed by central-api, plus its own poll), checks them (`deploy.sh --dry-run`)
 * and deploys them automatically (`deploy.sh deploy`: pull by digest, health check, automatic
 * rollback). It keeps its own run history on disk because a deploy restarts central-api.
 *
 * Only central-api talks to it: signed requests (signing.ts, key ID `central`) on central's
 * internal Docker network; it has no published port. central-api relays the Host Admin's
 * actions, mirrors finished runs into emails, and serves the console's Updates page.
 */
import { z } from 'zod';

/** Strict X.Y.Z, as release files and deploy.sh accept (no v prefix, no pre-release). */
export const updaterVersionSchema = z.string().regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/);

export const updaterRunKindSchema = z.enum(['check', 'deploy', 'rollback']);
export type UpdaterRunKind = z.infer<typeof updaterRunKindSchema>;

/** What started a run: the GitLab webhook, the updater's poll, a Host Admin, or an automatic deploy after a passed check. */
export const updaterTriggerSchema = z.enum(['webhook', 'poll', 'host_admin', 'auto']);
export type UpdaterTrigger = z.infer<typeof updaterTriggerSchema>;

/**
 * Run outcome. `refused`: the release failed verification (deploy.sh exit 3), nothing changed.
 * `rolled_back`: deployed, failed the health check, previous release restored (exit 1).
 * `failed`: anything else, including a failed rollback (exit 4: manual action needed).
 */
export const updaterRunStatusSchema = z.enum([
  'running',
  'succeeded',
  'refused',
  'rolled_back',
  'failed',
]);
export type UpdaterRunStatus = z.infer<typeof updaterRunStatusSchema>;

export const updaterStepSchema = z.object({
  /** One `deploy: …` line of deploy.sh, without the prefix. */
  text: z.string().max(500),
  at: z.iso.datetime(),
  error: z.boolean(),
});

export const updaterRunSummarySchema = z.object({
  /** Sortable and unique, e.g. `20261011T153000Z-deploy`. */
  id: z.string().regex(/^[0-9]{8}T[0-9]{6}Z-[a-z]+(-[0-9]+)?$/),
  kind: updaterRunKindSchema,
  /** Null for a rollback (deploy.sh decides the target). */
  version: updaterVersionSchema.nullable(),
  trigger: updaterTriggerSchema,
  /** `host_admin:<email>` for a console action, else `central:system`. */
  actor: z.string().max(200),
  status: updaterRunStatusSchema,
  exitCode: z.number().int().nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});
export type UpdaterRunSummary = z.infer<typeof updaterRunSummarySchema>;

/** One run with its steps and the tail of its output (at most UPDATER_LOG_MAX characters). */
export const updaterRunSchema = updaterRunSummarySchema.extend({
  steps: z.array(updaterStepSchema),
  log: z.string(),
});
export type UpdaterRun = z.infer<typeof updaterRunSchema>;

export const UPDATER_LOG_MAX = 65_536;
/** Runs kept in the updater's history (oldest dropped first). */
export const UPDATER_HISTORY_MAX = 50;

export const updaterStateSchema = z.object({
  /** What `deploy.sh status` reports as current, null before the first deploy. */
  current: updaterVersionSchema.nullable(),
  previous: updaterVersionSchema.nullable(),
  /** Newest release that passed a check (signature, content, dry run); null when none found. */
  latest: z.object({ version: updaterVersionSchema, checkedAt: z.iso.datetime() }).nullable(),
  /** Deploy a newer release automatically after its check passes (owner decision 2026-10-10). */
  autoDeploy: z.boolean(),
  /** A run is in progress: new deploys and rollbacks answer 409. */
  busy: z.boolean(),
  lastPollAt: z.iso.datetime().nullable(),
  /** Newest first, at most UPDATER_HISTORY_MAX. */
  runs: z.array(updaterRunSummarySchema),
});
export type UpdaterState = z.infer<typeof updaterStateSchema>;

// --- Updater HTTP API (private; every request signed by central-api, key ID `central`) ---

export const UPDATER_KEY_ID = 'central';

/** POST /v1/check: look for (or re-check) a release; `version` comes from the webhook hint. */
export const updaterCheckRequestSchema = z.object({
  version: updaterVersionSchema.optional(),
  trigger: updaterTriggerSchema,
  actor: z.string().max(200),
});
/** POST /v1/deploy */
export const updaterDeployRequestSchema = z.object({
  version: updaterVersionSchema,
  trigger: updaterTriggerSchema,
  actor: z.string().max(200),
});
/** POST /v1/rollback */
export const updaterRollbackRequestSchema = z.object({
  trigger: updaterTriggerSchema,
  actor: z.string().max(200),
});
/** 202 for check/deploy/rollback; a busy updater answers 409 with the error envelope `busy`. */
export const updaterAcceptedSchema = z.object({ runId: updaterRunSummarySchema.shape.id });

/** The updater's own routes (GET /v1/state, GET /v1/runs/:id, POST the three actions, GET /healthz unsigned). */
export const UPDATER_ROUTES = [
  { method: 'GET', path: '/healthz', signed: false },
  { method: 'GET', path: '/v1/state', signed: true },
  { method: 'GET', path: '/v1/runs/:id', signed: true },
  { method: 'POST', path: '/v1/check', signed: true },
  { method: 'POST', path: '/v1/deploy', signed: true },
  { method: 'POST', path: '/v1/rollback', signed: true },
] as const;

// --- Host Admin console (central-api /v1/admin/updates…) ---

/** GET /v1/admin/updates: the updater's state, or why it can't be reached. */
export const updatesViewSchema = z.object({
  reachable: z.boolean(),
  /** Set when unreachable; never contains secrets or addresses. */
  error: z.string().max(300).nullable(),
  state: updaterStateSchema.nullable(),
});
export type UpdatesView = z.infer<typeof updatesViewSchema>;

export const updatesDeployRequestSchema = z.object({ version: updaterVersionSchema });

// --- GitLab webhook (POST /v1/hooks/gitlab, public; X-Gitlab-Token checked) ---

/**
 * The fields read from a GitLab "Job events" payload. A finished `release-publish` job on a
 * `vX.Y.Z` tag is a hint that release X.Y.Z exists; the updater still downloads and verifies
 * it, so a forged hook changes nothing. Everything else is accepted and ignored.
 */
export const gitlabJobEventSchema = z.looseObject({
  object_kind: z.string(),
  ref: z.string().max(200).optional(),
  tag: z.boolean().optional(),
  build_name: z.string().max(200).optional(),
  build_status: z.string().max(50).optional(),
});
export const GITLAB_TOKEN_HEADER = 'x-gitlab-token';
export const RELEASE_PUBLISH_JOB = 'release-publish';

export const webhookAcceptedSchema = z.object({
  /** True when the hook named a release and the updater was asked to check it. */
  relayed: z.boolean(),
  version: updaterVersionSchema.nullable(),
});

/** `vX.Y.Z` tag of a successful release-publish job → X.Y.Z; anything else → null. */
export function releaseFromJobEvent(event: z.infer<typeof gitlabJobEventSchema>): string | null {
  if (event.object_kind !== 'build' || event.tag !== true) return null;
  if (event.build_name !== RELEASE_PUBLISH_JOB || event.build_status !== 'success') return null;
  const match = /^v(\d+\.\d+\.\d+)$/.exec(event.ref ?? '');
  const version = match?.[1];
  return version !== undefined && updaterVersionSchema.safeParse(version).success ? version : null;
}

// --- School update notice (LIVE L4) ---

/**
 * One email per release to a school's contact when the school is behind it. Shown per school
 * in the console; null when the school is current or no notice is due.
 */
export const updateNoticeSchema = z.object({
  version: updaterVersionSchema,
  status: z.enum(['sent', 'failed', 'no_contact', 'pending']),
  at: z.iso.datetime().nullable(),
});
export type UpdateNotice = z.infer<typeof updateNoticeSchema>;
